import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Service {
  id: string
  name: string
  type: string
  status: string
  uptime_percentage: number
  response_time_ms: number
  error_rate: number
  instances: number
  healthy_instances: number
}

interface Incident {
  id: string
  title: string
  description: string
  severity: string
  status: string
  affected_services: string[]
  started_at: string
  resolved_at: string | null
}

interface Alert {
  id: string
  type: string
  severity: string
  title: string
  message: string
  service: string
  triggered_at: string
  acknowledged: boolean
}

export default function SystemHealthDashboardPage() {
  const [services, setServices] = useState<Service[]>([])
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [metrics, setMetrics] = useState<any>(null)

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchMetrics, 30000)
    return () => clearInterval(interval)
  }, [])

  const fetchData = async () => {
    const [svcRes, incRes, alertRes, statsRes, metricsRes] = await Promise.all([
      fetch(API_BASE + '/system-health/services'),
      fetch(API_BASE + '/system-health/incidents'),
      fetch(API_BASE + '/system-health/alerts'),
      fetch(API_BASE + '/system-health/statistics'),
      fetch(API_BASE + '/system-health/metrics/current')
    ])
    if (svcRes.ok) setServices((await svcRes.json()).services || [])
    if (incRes.ok) setIncidents((await incRes.json()).incidents || [])
    if (alertRes.ok) setAlerts((await alertRes.json()).alerts || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
    if (metricsRes.ok) setMetrics(await metricsRes.json())
  }

  const fetchMetrics = async () => {
    const res = await fetch(API_BASE + '/system-health/metrics/current')
    if (res.ok) setMetrics(await res.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-500'
      case 'degraded': return 'bg-yellow-500'
      case 'down': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800'
      case 'warning': return 'bg-yellow-100 text-yellow-800'
      case 'minor': return 'bg-blue-100 text-blue-800'
      case 'info': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">System Health Dashboard</h1>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Services</div>
              <div className="text-2xl font-bold">{statistics.total_services}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Healthy</div>
              <div className="text-2xl font-bold text-green-600">{statistics.healthy_services}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Uptime</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.overall_uptime}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Incidents</div>
              <div className="text-2xl font-bold text-orange-600">{statistics.active_incidents}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Response</div>
              <div className="text-2xl font-bold">{statistics.average_response_time_ms}ms</div>
            </div>
          </div>
        )}

        {metrics && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">CPU Usage</span>
                <span className="font-bold">{metrics.cpu_usage}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${metrics.cpu_usage > 80 ? 'bg-red-500' : metrics.cpu_usage > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                     style={{ width: `${metrics.cpu_usage}%` }}></div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">Memory</span>
                <span className="font-bold">{metrics.memory_usage}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${metrics.memory_usage > 80 ? 'bg-red-500' : metrics.memory_usage > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                     style={{ width: `${metrics.memory_usage}%` }}></div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">Disk</span>
                <span className="font-bold">{metrics.disk_usage}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${metrics.disk_usage > 80 ? 'bg-red-500' : metrics.disk_usage > 60 ? 'bg-yellow-500' : 'bg-green-500'}`}
                     style={{ width: `${metrics.disk_usage}%` }}></div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500 mb-1">Active Connections</div>
              <div className="text-2xl font-bold">{metrics.active_connections?.toLocaleString()}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-4 mb-8">
          {services.map(svc => (
            <div key={svc.id} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-3 h-3 rounded-full ${getStatusColor(svc.status)}`}></div>
                <span className="font-semibold">{svc.name}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-gray-500">Uptime</div>
                  <div className="font-medium">{svc.uptime_percentage}%</div>
                </div>
                <div>
                  <div className="text-gray-500">Response</div>
                  <div className="font-medium">{svc.response_time_ms}ms</div>
                </div>
                <div>
                  <div className="text-gray-500">Error Rate</div>
                  <div className="font-medium">{svc.error_rate}%</div>
                </div>
                <div>
                  <div className="text-gray-500">Instances</div>
                  <div className="font-medium">{svc.healthy_instances}/{svc.instances}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Incidents</h2></div>
            <div className="divide-y">
              {incidents.map(incident => (
                <div key={incident.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(incident.severity)}`}>{incident.severity}</span>
                      <span className="font-semibold">{incident.title}</span>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${incident.status === 'resolved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {incident.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-2">{incident.description}</p>
                  <div className="text-xs text-gray-400">
                    Started: {new Date(incident.started_at).toLocaleString()}
                    {incident.resolved_at && ` • Resolved: ${new Date(incident.resolved_at).toLocaleString()}`}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Alerts</h2></div>
            <div className="divide-y">
              {alerts.map(alert => (
                <div key={alert.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(alert.severity)}`}>{alert.severity}</span>
                      <span className="font-semibold ml-2">{alert.title}</span>
                    </div>
                    {!alert.acknowledged && (
                      <button className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200">Acknowledge</button>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mb-1">{alert.message}</p>
                  <div className="text-xs text-gray-400">
                    {alert.service} • {new Date(alert.triggered_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
